import React, { useEffect } from 'react';
import { Phone, Mail, MapPin, Award, Users, Heart, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

export const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1492691527719-9d1e07e534b4)'
          }}>

          <div className="absolute inset-0 bg-gradient-to-r from-sky-900/80 to-emerald-900/80"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">About Sky Trips</h1>
          <p className="!text-xl">Your Trusted Travel Partner Since 2012 </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Story</h2>
              <div className="w-24 h-1 bg-gradient-to-r from-sky-500 to-emerald-500 mx-auto mb-6"></div>
            </div>
            
            <div className="space-y-6 text-gray-700 text-lg leading-relaxed">
              <p>
                Welcome to <span className="font-semibold text-sky-600">Sky Trips</span>, your gateway to unforgettable travel experiences. 
                Based in Thane, Maharashtra, we are a dedicated team of travel enthusiasts committed to making your dream 
                vacations a reality.
              </p>
              
              <p>
                At Sky Trips, we believe that travel is not just about visiting new places – it's about creating 
                memories that last a lifetime. Whether you're planning a romantic honeymoon, a family vacation, 
                a spiritual pilgrimage, or an adventurous group tour, we have the perfect package for you.
              </p>
              
              <p>
                Our experienced travel planners work tirelessly to curate personalized itineraries that match your 
                preferences, budget, and travel style. We handle all the details – from accommodation and transportation 
                to sightseeing and activities – so you can focus on enjoying your journey.
              </p>
              
              <p>
                With a strong network of partners across India and internationally, we ensure that you get the best 
                value for your money without compromising on quality. Your satisfaction and safety are our top priorities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-gradient-to-br from-sky-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-gradient-to-br from-sky-500 to-emerald-500 rounded-full flex items-center justify-center mb-4">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Our Mission</h3>
              <p className="text-gray-700 leading-relaxed">
                To provide exceptional travel experiences that exceed expectations, offering personalized service, 
                competitive pricing, and unwavering commitment to customer satisfaction. We strive to make every 
                journey memorable and hassle-free.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-gradient-to-br from-sky-500 to-emerald-500 rounded-full flex items-center justify-center mb-4">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Our Vision</h3>
              <p className="text-gray-700 leading-relaxed">
                To become India's most trusted and preferred travel agency, known for creating life-changing travel 
                experiences. We envision a world where everyone has the opportunity to explore, discover, and connect 
                with diverse cultures and destinations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why We're Different */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Why We're Different</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-sky-500 to-emerald-500 mx-auto"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-sky-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Expert Guidance</h3>
              <p className="text-gray-600">
                Our team has years of experience in the travel industry. We know the destinations inside out and 
                provide expert advice to help you make the best choices.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-sky-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Personalized Service</h3>
              <p className="text-gray-600">
                We don't believe in one-size-fits-all. Every traveler is unique, and so are their needs. We customize 
                every package to match your specific requirements.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-sky-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Passionate Team</h3>
              <p className="text-gray-600">
                We're not just travel agents – we're travel lovers. Our passion for exploration drives us to create 
                exceptional experiences for every client.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-r from-sky-600 to-emerald-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Get in Touch with Us</h2>
              <p className="text-xl mb-8">
                Have questions? Want to plan your next adventure? We're here to help!
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg text-center">
                <MapPin className="w-8 h-8 mx-auto mb-3" />
                <p className="text-sm">
                  Office No. 1, Gulmohar CHS,<br />
                  Thane (West) – 400602
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg text-center">
                <Phone className="w-8 h-8 mx-auto mb-3" />
                <a href="tel:+919833280079" className="hover:text-sky-200 transition-colors">
                  +91 9833280079
                </a>
                <br />
                <a href="tel:+919920995700" className="hover:text-sky-200 transition-colors">
                  +91 9920995700
                </a>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg text-center">
                <Mail className="w-8 h-8 mx-auto mb-3" />
                <a href="mailto:info@skytrips.world" className="hover:text-sky-200 transition-colors">
                  info@skytrips.world
                </a>
              </div>
            </div>
            
            <div className="text-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-sky-600 hover:bg-gray-100 px-8 py-6 text-lg">
                  Contact Us Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>);

};